/**
 * Errors List Component
 *
 * Shows detected CV errors grouped by type (typo, grammar, formatting,
 * encoding, inconsistency).
 *
 * Layout:
 *   !! Typo: "Щкфсду Fusion" -> "Oracle Fusion"
 *   !  Grammar: "the a new system" -> "a new system"
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ErrorsListProps } from '../types';
import type { DetectedErrorType } from '../../../types/ats-report';

const typeLabels: Record<DetectedErrorType, string> = {
  typo: 'Typo',
  grammar: 'Grammar',
  formatting: 'Formatting',
  encoding: 'Encoding',
  inconsistency: 'Inconsistency',
};

const typeIconClasses: Record<DetectedErrorType, string> = {
  typo: 'ats-report-error__icon--typo',
  grammar: 'ats-report-error__icon--grammar',
  formatting: 'ats-report-error__icon--formatting',
  encoding: 'ats-report-error__icon--encoding',
  inconsistency: 'ats-report-error__icon--inconsistency',
};

export const ErrorsList: React.FC<ErrorsListProps> = ({ errors }) => {
  if (errors.length === 0) return null;

  return (
    <div className="ats-report-errors">
      <div className="ats-report-errors__header">
        <AlertTriangle className="ats-report-errors__header-icon" />
        <h4 className="ats-report-errors__title">Detected Errors</h4>
        <span className="ats-report-errors__count">{errors.length}</span>
      </div>

      <ul className="ats-report-errors__list">
        {errors.map((error, i) => (
          <li key={i} className="ats-report-error">
            <span className={`ats-report-error__icon ${typeIconClasses[error.type]}`}>
              <AlertTriangle className="ats-report-error__icon-svg" />
            </span>
            <div className="ats-report-error__content">
              <span className="ats-report-error__type">{typeLabels[error.type]}</span>
              <span className="ats-report-error__text">
                <span className="ats-report-error__original">"{error.text}"</span>
                <span className="ats-report-error__arrow">&rarr;</span>
                <span className="ats-report-error__suggestion">"{error.suggestion}"</span>
              </span>
              {error.location && (
                <span className="ats-report-error__location">in {error.location}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

ErrorsList.displayName = 'ErrorsList';

export default ErrorsList;
